import { Component, Fragment, h, Listen, Method, Prop, State } from '@stencil/core';
import { markComponentAsLoaded } from '../../../redux/actions';
import { formatDateTimeAndLocalize, hourToLocalizedStringHour } from '../../../utils/utils';
import { rentwareDateExpanded } from './expanded/rentware-date-expanded';
import { store } from '../../../redux/store';
import { RootState } from '../../../redux/reducers';
import { Unsubscribe } from '@stencil/redux';
import { TimeSlotPicker, Translations } from '../../../redux/types';
import { trans } from '../../../utils/trans';
import { CalendarMode, DropOffDateIcon, PickUpDateIcon } from './utils';
import { DaysAvailability } from '../../../types/types';

@Component({
  tag: 'rentware-date-select',
  styleUrl: 'style.sass',
  shadow: true,
})
export class RentwareDateSelect {
  @Prop() calendarMode: CalendarMode = CalendarMode.SingleDayPickup; // Only for icon and tooltips
  @Prop() locale: string;
  @Prop() translations: Translations;
  @Prop() label: string;
  @Prop() emptyDatePlaceholder: string;
  @Prop() withTime: boolean;
  @Prop() withDateRange: boolean;
  @Prop() loadingDays: boolean;
  @Prop() loadingPickUpHours: boolean;
  @Prop() loadingDropOffHours: boolean;
  @Prop() hasValidationError: boolean = false;
  @Prop() firstAvailableDate: Date;
  @Prop() lastAvailableDate: Date;
  @Prop() disabled: boolean;
  @Prop() showNotAvailableOverlay: boolean;

  @Prop() bookingDate: Date;
  @Prop() monthAtStart: Date;
  @Prop() daysAvailability: DaysAvailability;
  @Prop() onDayClick: (day: Date) => void;
  @Prop() onMonthChange: (month: Date) => void = () => {};
  @Prop() onFocus: () => void = () => {};
  @Prop() bookingTimeHint: string;
  @Prop() bookingTime: string;
  @Prop() bookingHours: string[];
  @Prop() bookingTimePlaceholder: string;
  @Prop() onBookingTimeChange: (time: string) => void;

  @Prop() endTime: string;
  @Prop() endTimeHint: string;
  @Prop() endTimePlaceholder: string;
  @Prop() endHours: string[];
  @Prop() onEndTimeChange: (time: string) => void;
  @Prop() onResetCalendar: (day?: Date) => void;

  @Prop() timeSlotPicker: TimeSlotPicker;
  @Prop({ mutable: true }) loadedComponents;

  @State() isOpen: boolean = false;
  @State() positionAbsoluteRight: boolean = false;

  outerWrapperElement: HTMLElement;
  expandedPickerElement: HTMLElement;

  markComponentAsLoaded!: typeof markComponentAsLoaded;
  unsubscribe!: Unsubscribe;

  componentWillLoad() {
    this.unsubscribe = store.mapStateToProps(this, (state: RootState) => {
      return {
        loadedComponents: state.app.loadedComponents,
      };
    });

    store.mapDispatchToProps(this, {
      markComponentAsLoaded,
    });
  }

  disconnectedCallback() {
    this.unsubscribe();
  }

  // Timed, result of mechanic
  @Listen('closeDateSelect', { capture: true, target: 'body' })
  closeDateSelect() {
    if (this.isOpen) {
      setTimeout(() => (this.isOpen = false), 500);
    }
  }

  // Instant, respond for user actions
  @Listen('clickOutsideCalendar', { capture: true })
  clickOutsideCompletedHandler() {
    this.collapse();
  }

  handleExpandedDidLoad = () => {
    const position = this.expandedPickerElement.getBoundingClientRect();

    if (position.top < 0 || position.bottom > window.innerHeight) {
      this.expandedPickerElement.scrollIntoView({ block: 'end', behavior: 'smooth' });
    }
  };

  togglesOpen = () => {
    if (this.disabled) {
      return;
    }

    if (!this.isOpen) {
      this.onFocus();
      this.handleExpandedWillLoad();
    }
    this.isOpen = !this.isOpen;
  };

  //check if space to the left is bigger than to the right
  //if not, set position to absolute right
  handleExpandedWillLoad = () => {
    const position = this.outerWrapperElement.getBoundingClientRect();
    const spaceToTheLeft = position.left;
    const spaceToTheRight = window.innerWidth - position.right;

    this.positionAbsoluteRight = spaceToTheLeft > spaceToTheRight;
  };

  //todo: make this using event
  @Method()
  collapse() {
    this.isOpen = false;
  }

  get isLoading() {
    const isWaitingForDependencies = this.isOpen && !this.loadedComponents[rentwareDateExpanded.name];
    return isWaitingForDependencies || this.loadingDays;
  }

  get timeSlotLabel() {
    return trans('articleBooking.timeSlotLabel', this.locale, this.translations);
  }

  get labelIncludingTimeSlot() {
    return this.timeSlotPicker ? this.timeSlotLabel : this.label;
  }

  get selectStyle(): string {
    return (this.disabled && 'SelectContainer Disabled') || 'SelectContainer';
  }

  render() {
    return (
      <div class={`OuterContainer ${this.hasValidationError && 'NotValid'}`} ref={el => (this.outerWrapperElement = el as HTMLElement)}>
        {this.isLoading && <rentware-spinner withOverlay={true} />}

        <div class={this.selectStyle} tabIndex={0} onClick={this.togglesOpen}>
          <div class="SelectLabel">{this.labelIncludingTimeSlot}</div>

          <div class="LeftInner">
            {this.calendarMode === CalendarMode.SingleDayPickup && PickUpDateIcon}
            {this.calendarMode === CalendarMode.SingleDayDropOff && DropOffDateIcon}

            <div class="DateContainer">
              {/*** with date slot ***/}
              {this.timeSlotPicker && (
                <Fragment>
                  {(!this.bookingDate && <Fragment>{this.emptyDatePlaceholder}</Fragment>) || (
                    <Fragment>
                      {formatDateTimeAndLocalize(this.bookingDate, null, null, null)}
                      ,&nbsp;
                      {(!this.timeSlotPicker.selectedOption && <Fragment>{this.timeSlotLabel}</Fragment>) || (
                        <Fragment>{this.timeSlotPicker.options.find(period => period.value === this.timeSlotPicker.selectedOption).label}</Fragment>
                      )}
                    </Fragment>
                  )}
                </Fragment>
              )}

              {/*** without date slot ***/}
              {!this.timeSlotPicker && (
                <Fragment>
                  {/*** with no time - single or multi day ***/}
                  {!this.withTime && (
                    <Fragment>
                      {(!this.bookingDate && <Fragment>{this.emptyDatePlaceholder}</Fragment>) || (
                        <Fragment>{formatDateTimeAndLocalize(this.bookingDate, null, null, null)}</Fragment>
                      )}
                    </Fragment>
                  )}

                  {/*** with time + width date range ***/}
                  {this.withTime &&
                    this.withDateRange &&
                    ((!this.bookingDate && <Fragment>{this.emptyDatePlaceholder}</Fragment>) || (
                      <Fragment>
                        {(!this.bookingTime && (
                          <Fragment>
                            {(this.isOpen && (
                              <Fragment>
                                {formatDateTimeAndLocalize(this.bookingDate, null, null, null)}, {this.bookingTimePlaceholder}
                              </Fragment>
                            )) || (
                              <Fragment>
                                {formatDateTimeAndLocalize(this.bookingDate, null, null, null)}, <span class="Error">{this.bookingTimePlaceholder}</span>
                              </Fragment>
                            )}
                          </Fragment>
                        )) || <Fragment>{formatDateTimeAndLocalize(this.bookingDate, this.bookingTime, null, this.withTime)}</Fragment>}
                      </Fragment>
                    ))}

                  {/*** with time + width no date range ***/}
                  {this.withTime &&
                    !this.withDateRange &&
                    ((!this.bookingDate && <Fragment>{this.emptyDatePlaceholder}</Fragment>) || (
                      <Fragment>
                        {(!this.bookingTime && (
                          <Fragment>
                            {(!this.isOpen && (
                              <Fragment>
                                {formatDateTimeAndLocalize(this.bookingDate, null, null, null)}, <span class="Error"> {this.bookingTimePlaceholder}</span>
                              </Fragment>
                            )) || (
                              <Fragment>
                                {formatDateTimeAndLocalize(this.bookingDate, null, null, null)}, {this.bookingTimePlaceholder}
                              </Fragment>
                            )}
                          </Fragment>
                        )) || (
                          <Fragment>
                            {(!this.endTime && (
                              <Fragment>
                                {(!this.isOpen && (
                                  <Fragment>
                                    {formatDateTimeAndLocalize(this.bookingDate, this.bookingTime, null, this.withTime)}

                                    <svg width="14" height="10" viewBox="0 0 14 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M1 5H13M13 5L8.97605 1M13 5L8.97605 9" stroke="#B5B4BD" stroke-linecap="round" stroke-linejoin="round" />
                                    </svg>

                                    <span class="Error">{this.endTimePlaceholder}</span>
                                  </Fragment>
                                )) || (
                                  <Fragment>
                                    {formatDateTimeAndLocalize(this.bookingDate, this.bookingTime, null, this.withTime)}

                                    <svg width="14" height="10" viewBox="0 0 14 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M1 5H13M13 5L8.97605 1M13 5L8.97605 9" stroke="#B5B4BD" stroke-linecap="round" stroke-linejoin="round" />
                                    </svg>

                                    {this.endTimePlaceholder}
                                  </Fragment>
                                )}
                              </Fragment>
                            )) || (
                              <Fragment>
                                {formatDateTimeAndLocalize(this.bookingDate, this.bookingTime, null, this.withTime)}

                                <svg width="14" height="10" viewBox="0 0 14 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M1 5H13M13 5L8.97605 1M13 5L8.97605 9" stroke-linecap="round" stroke-linejoin="round" />
                                </svg>

                                {hourToLocalizedStringHour(this.endTime)}
                              </Fragment>
                            )}
                          </Fragment>
                        )}
                      </Fragment>
                    ))}
                </Fragment>
              )}
            </div>
          </div>
          <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 1L5 5L9 1" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </div>
        {this.isOpen && (
          <div class={`ExpandedContainerOuter ${this.positionAbsoluteRight ? 'Right' : ''}`} ref={el => (this.expandedPickerElement = el as HTMLElement)}>
            <div class="ExpandedContainer">
              <rentware-date-expanded
                calendarMode={this.calendarMode}
                locale={this.locale}
                translations={this.translations}
                withDateRange={this.withDateRange}
                withTime={this.withTime}
                loadingPickUpHours={this.loadingPickUpHours}
                loadingDropOffHours={this.loadingDropOffHours}
                isLoading={this.isLoading}
                onExpandedDidLoad={this.handleExpandedDidLoad}
                showNotAvailableOverlay={this.showNotAvailableOverlay}
                onComponentLoaded={name => this.markComponentAsLoaded(name)}
                // shared
                bookingDate={this.bookingDate}
                monthAtStart={this.monthAtStart}
                timeSlotPicker={this.timeSlotPicker}
                firstAvailableDate={this.firstAvailableDate}
                lastAvailableDate={this.lastAvailableDate}
                daysAvailability={this.daysAvailability}
                onMonthChange={this.onMonthChange}
                onDayClick={this.onDayClick}
                // pickup
                bookingTimeHint={this.bookingTimeHint}
                bookingTime={this.bookingTime}
                bookingHours={this.bookingHours}
                onBookingTimeChange={this.onBookingTimeChange}
                // drop off
                endTime={this.endTime}
                endTimeHint={this.endTimeHint}
                endHours={this.endHours}
                onEndTimeChange={this.onEndTimeChange}
                onResetCalendar={this.onResetCalendar}
              />
            </div>
          </div>
        )}
      </div>
    );
  }
}
